import React, { useState, useContext, useRef, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';

import SidePanel from '../components/Common/SidePanel';
import MobileMenuToggle from '../components/Common/MobileMenuToggle';
import { Barcode, Plus, AlertCircle, CheckCircle, Loader, Camera, X, Shield } from 'lucide-react';
import { barcodeService, recordService } from '../services/api';
import Quagga from '@ericblade/quagga2';

const Scan = () => {
  const { user } = useContext(AuthContext);
  const { isCollapsed } = useSidebar();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [useCamera, setUseCamera] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionRequested, setPermissionRequested] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);
  const quaggaRef = useRef(null);
  const mobileQuaggaRef = useRef(null);
  const [formData, setFormData] = useState({
    vehicleNo: '',
    vehicleType: 'Type 1',
    dateOfEntry: new Date().toISOString().split('T')[0],
    timeOfEntry: new Date().toTimeString().slice(0, 5),
    notes: ''
  });

  useEffect(() => {
    if (useCamera && !cameraActive) {
      initCamera();
    }
    return () => {
      if (cameraActive) {
        Quagga.stop();
      }
    };
  }, [useCamera, cameraActive]);

  const initCamera = async () => {
    try {
      setPermissionRequested(true);
      // Request camera permission first (works on iOS and Android)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment',
            width: { min: 640 },
            height: { min: 480 }
          } 
        });
        // Stop the stream immediately - we just needed to request permission
        stream.getTracks().forEach(track => track.stop());
        setShowPermissionModal(false);
      } catch (permissionErr) {
        if (permissionErr.name === 'NotAllowedError') {
          setError('Camera permission denied. Please enable camera access in your device settings and try again.');
        } else if (permissionErr.name === 'NotFoundError') {
          setError('No camera found on your device.');
        } else {
          setError('Unable to access camera. Please check your device settings.');
        }
        setUseCamera(false);
        setPermissionRequested(false);
        return;
      }

      // Determine which ref to use based on screen size
      const isMobile = window.innerWidth < 1024; // lg breakpoint
      const targetRef = isMobile ? mobileQuaggaRef : quaggaRef;

      Quagga.init(
        {
          inputStream: {
            type: 'LiveStream',
            constraints: {
              width: { min: 640 },
              height: { min: 480 },
              facingMode: 'environment',
              aspectRatio: { min: 4 / 3, max: 16 / 9 }
            },
            target: targetRef.current
          },
          decoder: {
            workers: 2,
            debug: false,
            multiple: false
          },
          locate: true,
          frequency: 10,
          maxImageSize: 5000000
        },
        (err) => {
          if (err) {
            setError('Failed to initialize camera. Please use manual input.');
            setUseCamera(false);
            setPermissionRequested(false);
            return;
          }
          Quagga.start();
          setCameraActive(true);
          setupQuaggaListeners();
        }
      );
    } catch (err) {
      setError('Failed to initialize camera. Please use manual input.');
      setUseCamera(false);
      setPermissionRequested(false);
    }
  };

  const setupQuaggaListeners = () => {
    Quagga.onDetected(handleBarcodeDetected);
  };

  const handleBarcodeDetected = (result) => {
    if (result.codeResult && result.codeResult.code) {
      const barcode = result.codeResult.code;
      
      // Close camera IMMEDIATELY - synchronously, before any async calls
      Quagga.stop();
      Quagga.offDetected();
      setCameraActive(false);
      setUseCamera(false);
      
      // Validate barcode (async - but camera is already closed)
      performBarcodeValidation(barcode);
    }
  };

  const performBarcodeValidation = async (code) => {
    setScanning(true);
    setError('');
    setBarcodeInput('');

    try {
      // Extract numeric portion from barcode (in case of "RO - 13787" format)
      const numericPortion = code.replace(/\D/g, '');
      const searchCode = numericPortion || code; // Use numeric portion if available, otherwise use original
      const response = await barcodeService.validate(searchCode);
      
      setScannedData({
        barcodeNumber: response.data?.data?.barcodeNumber || code,
        scanned: true
      });
      
      // Reset form with new date/time
      setFormData({
        vehicleNo: '',
        vehicleType: 'Type 1',
        dateOfEntry: new Date().toISOString().split('T')[0],
        timeOfEntry: new Date().toTimeString().slice(0, 5),
        notes: ''
      });
    } catch (err) {
      // Get detailed error information from backend
      const errorDetails = err.response?.data?.details;
      const status = errorDetails?.status;
      const message = errorDetails?.message || err.response?.data?.error;
      
      let errorMessage = err.response?.data?.error || 'Barcode validation failed';
      
      // Check for specific status (handle both lowercase and uppercase)
      if (status === 'RECORD_EXISTS' || status === 'record_exists') {
        errorMessage = `Barcode "${code}" already has a service record. Please use "Scan & Update" to update the existing record instead.`;
      } else if (status === 'NOT_ACTIVATED' || status === 'not_activated') {
        errorMessage = `Barcode "${code}" is NOT ACTIVATED. Please contact admin to activate this barcode before use.`;
      } else if (status === 'DEACTIVATED' || status === 'deactivated') {
        errorMessage = `Barcode "${code}" has been DEACTIVATED and cannot be used.`;
      } else if (status === 'NOT_FOUND' || status === 'not_found') {
        errorMessage = `Barcode "${code}" not found in the system. Please verify and try again.`;
      } else if (message) {
        errorMessage = `${message}`;
      }
      
      setError(errorMessage);
    } finally {
      setScanning(false);
      inputRef.current?.focus();
    }
  };

  const stopCamera = () => {
    Quagga.stop();
    Quagga.offDetected();
    setCameraActive(false);
    setUseCamera(false);
    setBarcodeInput('');
  };

  const validateBarcode = async (code) => {
    if (!code.trim()) {
      setError('Please enter a barcode');
      return;
    }

    await performBarcodeValidation(code);
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!barcodeInput.trim()) {
      setError('Please enter a barcode');
      return;
    }

    validateBarcode(barcodeInput);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!scannedData) return;

    setSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      await recordService.create({
        barcodeNumber: scannedData.barcodeNumber,
        vehicleNo: formData.vehicleNo,
        vehicleType: formData.vehicleType,
        dateOfEntry: formData.dateOfEntry,
        timeOfEntry: formData.timeOfEntry,
        notes: formData.notes || null
      });

      setSuccess(true);
      setScannedData(null);
      setFormData({
        vehicleNo: '',
        vehicleType: 'Type 1',
        dateOfEntry: new Date().toISOString().split('T')[0],
        timeOfEntry: new Date().toTimeString().slice(0, 5),
        notes: ''
      });
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create record');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <MobileMenuToggle onClick={() => setIsSidebarOpen(true)} isSidebarOpen={isSidebarOpen} />
      <SidePanel isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Camera Permission Modal */}
      {showPermissionModal && !cameraActive && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-3 text-center">Enable Camera Access</h2>
            
            <p className="text-slate-700 text-center mb-6 font-medium">
              This app needs camera access to scan barcodes. Please click "Allow" when prompted by your device.
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-slate-900 mb-3">What to expect:</h3>
              <ul className="text-sm text-slate-700 space-y-2">
                <li className="flex gap-2">
                  <span className="font-bold text-blue-600">1.</span>
                  <span>Your device will show a permission dialog</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-blue-600">2.</span>
                  <span>Tap "Allow" or "Allow Always" to enable camera</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-blue-600">3.</span>
                  <span>Camera will open and scan barcodes</span>
                </li>
              </ul>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => initCamera()}
                disabled={permissionRequested}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition disabled:from-slate-400 disabled:to-slate-500 font-bold shadow-lg"
              >
                {permissionRequested ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Requesting Permission...
                  </>
                ) : (
                  <>
                    <Camera className="w-5 h-5" />
                    Enable Camera
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  setShowPermissionModal(false);
                  setUseCamera(false);
                  setPermissionRequested(false);
                }}
                disabled={permissionRequested}
                className="w-full py-3 rounded-xl border-2 border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 transition disabled:opacity-50"
              >
                Use Manual Entry Instead
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-300">
        <main className="flex-1 overflow-y-auto pt-28 md:pt-0">
          <div className="p-6 md:p-8 mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Create Service Record</h1>
              <p className="text-slate-600">Scan barcode and create a new service record</p>
            </div>

            {success && (
              <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-xl flex gap-3 shadow-sm">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-green-800 font-medium">Service record created successfully!</p>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-300 rounded-xl flex gap-3 shadow-sm">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Barcode Scanner - Full width on mobile, left column on desktop */}
              <div className="lg:col-span-1 bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg p-6 md:p-8 border border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                    <Barcode className="w-6 h-6 text-white" />
                  </div>
                  Scan Barcode
                </h3>

                {useCamera ? (
                  <div className="space-y-4">
                    <div className="relative w-full rounded-2xl overflow-hidden bg-black shadow-xl border-4 border-blue-600" style={{ aspectRatio: '4/3' }}>
                      <div ref={quaggaRef} style={{ width: '100%', height: '100%' }} />
                      {cameraActive && (
                        <div className="absolute top-4 right-4 flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                          <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse"></div>
                          Ready
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-gradient-to-r from-blue-100 to-indigo-100 border-2 border-blue-400 rounded-xl">
                      <p className="text-sm font-semibold text-blue-900">
                        Point camera at barcode
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => stopCamera()}
                      className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-slate-600 to-slate-700 text-white py-3 rounded-xl hover:from-slate-700 hover:to-slate-800 transition shadow-md font-semibold text-base"
                    >
                      <X className="w-5 h-5" />
                      Close Camera
                    </button>
                  </div>
                ) : scannedData ? (
                  <div className="space-y-4">
                    <div className="p-5 bg-gradient-to-br from-green-50 to-emerald-50 border-3 border-green-400 rounded-2xl shadow-sm">
                      <p className="text-sm font-semibold text-green-900 mb-2">Successfully Scanned</p>
                      <p className="text-2xl font-mono font-bold text-green-700 mb-3">{scannedData.barcodeNumber}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setScannedData(null);
                        setFormData({
                          vehicleNo: '',
                          vehicleType: 'Type 1',
                          dateOfEntry: new Date().toISOString().split('T')[0],
                          timeOfEntry: new Date().toTimeString().slice(0, 5),
                          notes: ''
                        });
                        setBarcodeInput('');
                        inputRef.current?.focus();
                      }}
                      className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-blue-500 text-white py-3 rounded-xl hover:from-indigo-600 hover:to-blue-600 transition shadow-md font-semibold text-base"
                    >
                      <Barcode className="w-5 h-5" />
                      Scan Next Barcode
                    </button>
                  </div>
                ) : (
                  <>
                    <form onSubmit={handleScan} className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Manual Barcode Entry</label>
                        <input
                          ref={inputRef}
                          type="text"
                          placeholder="Enter barcode..."
                          value={barcodeInput}
                          onChange={(e) => setBarcodeInput(e.target.value)}
                          autoFocus
                          className="w-full px-5 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-base font-semibold transition bg-white shadow-sm"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={scanning || !!scannedData}
                        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition disabled:from-slate-400 disabled:to-slate-500 font-bold text-base shadow-md"
                      >
                        {scanning ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Validating...
                          </>
                        ) : (
                          <>Scan</>
                        )}
                      </button>
                    </form>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-300"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-gradient-to-br from-white to-slate-50 text-slate-500 font-medium">OR</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setShowPermissionModal(true);
                        setUseCamera(true);
                      }}
                      disabled={scanning || !!scannedData}
                      className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-xl hover:from-green-600 hover:to-emerald-700 transition disabled:from-slate-400 disabled:to-slate-500 font-bold text-lg shadow-lg transform hover:scale-105"
                    >
                      <Camera className="w-6 h-6" />
                      Open Camera
                    </button>
                  </>
                )}
              </div>

              {/* Record Creation Form - Right 2 columns on desktop, full width on mobile */}
              {scannedData && (
                <div className="lg:col-span-2 bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg p-6 md:p-8 border border-slate-200">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                      <Plus className="w-6 h-6 text-white" />
                    </div>
                    Service Details
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Barcode Badge */}
                    <div className="p-6 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-3 border-blue-300 rounded-2xl shadow-sm">
                      <p className="text-xs text-blue-600 font-bold uppercase tracking-wider mb-2">Barcode</p>
                      <p className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{scannedData.barcodeNumber}</p>
                    </div>

                    {/* Vehicle Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-slate-800 mb-2 uppercase tracking-wide">Vehicle Number *</label>
                        <input
                          type="text"
                          placeholder="e.g., MH-02-AB-1234"
                          value={formData.vehicleNo}
                          onChange={(e) => setFormData({...formData, vehicleNo: e.target.value.toUpperCase()})}
                          required
                          className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium transition bg-white shadow-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-slate-800 mb-2 uppercase tracking-wide">Vehicle Type *</label>
                        <select
                          value={formData.vehicleType}
                          onChange={(e) => setFormData({...formData, vehicleType: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium transition bg-white shadow-sm"
                        >
                          <option>Type 1</option>
                          <option>Type 2</option>
                          <option>Type 3</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-slate-800 mb-2 uppercase tracking-wide">Date of Entry *</label>
                        <input
                          type="date"
                          value={formData.dateOfEntry}
                          onChange={(e) => setFormData({...formData, dateOfEntry: e.target.value})}
                          required
                          className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium transition bg-white shadow-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-slate-800 mb-2 uppercase tracking-wide">Time of Entry *</label>
                        <input
                          type="time"
                          value={formData.timeOfEntry}
                          onChange={(e) => setFormData({...formData, timeOfEntry: e.target.value})}
                          required
                          className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium transition bg-white shadow-sm"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-bold text-slate-800 mb-3 uppercase tracking-wide">Notes</label>
                      <textarea
                        rows={4}
                        placeholder="Add any additional notes..."
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="w-full px-5 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium transition bg-white shadow-sm resize-none"
                      />
                    </div>

                    {/* Submit Button - Large and prominent */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-xl hover:from-green-600 hover:to-emerald-700 transition disabled:from-slate-400 disabled:to-slate-500 font-bold text-lg shadow-lg transform hover:scale-105"
                    >
                      {submitting ? (
                        <>
                          <Loader className="w-6 h-6 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>Create Record</>
                      )}
                    </button>
                  </form>
                </div>
              )}

              {/* Information Card - Shows on desktop when no barcode scanned */}
              {!scannedData && (
                <div className="hidden lg:block lg:col-span-2 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl shadow-lg p-8 border border-indigo-200">
                  <div className="h-full flex flex-col justify-between">
                    <div>
                      <h3 className="text-3xl font-bold text-slate-900 mb-6">How It Works</h3>
                      
                      <div className="space-y-6">
                        {/* Step 1 */}
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                            <span className="text-white font-bold text-xl">1</span>
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-slate-900 mb-2">Scan the Barcode</h4>
                            <p className="text-slate-700 font-medium">Use the camera or manually enter the barcode number in the field on the left.</p>
                          </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                            <span className="text-white font-bold text-xl">2</span>
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-slate-900 mb-2">Enter Vehicle Details</h4>
                            <p className="text-slate-700 font-medium">Fill in the serial number, vehicle number, type, and entry time.</p>
                          </div>
                        </div>

                        {/* Step 3 */}
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                            <span className="text-white font-bold text-xl">3</span>
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-slate-900 mb-2">Create Record</h4>
                            <p className="text-slate-700 font-medium">Add notes if needed, then click the Create button to save the new service record.</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Tips */}
                    <div className="mt-8 pt-8 border-t-2 border-indigo-300">
                      <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-4">Quick Tips</h4>
                      <ul className="space-y-3 text-sm text-slate-700 font-medium">
                        <li className="flex gap-2">
                          <span className="text-indigo-600">•</span>
                          <span>Use the camera for quick scanning in the field</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="text-indigo-600">•</span>
                          <span>Manual entry is available for offline situations</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="text-indigo-600">•</span>
                          <span>All vehicle details are required</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Floating Camera at Bottom on Mobile */}
      {useCamera && (
        <div className="fixed inset-0 lg:hidden z-50 flex flex-col">
          {/* Full screen overlay */}
          <div className="flex-1 bg-black relative">
            <div ref={mobileQuaggaRef} style={{ width: '100%', height: '100%' }} />
            
            {/* Status indicator */}
            {cameraActive && (
              <div className="absolute top-6 left-6 flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse"></div>
                Ready to Scan
              </div>
            )}
          </div>

          {/* Bottom control button */}
          <button
            type="button"
            onClick={() => stopCamera()}
            className="bg-slate-900 border-t border-slate-700 px-4 py-4 text-white flex items-center justify-center gap-3 font-bold text-lg hover:bg-black transition"
          >
            <X className="w-6 h-6" />
            Close Camera
          </button>
        </div>
      )}
    </div>
  );
};

export default Scan;
